import type { ValidationContext, ValidationIssue, ValidationRule } from "../types/validation";

export const DEFAULT_ASYNC_VALIDATION_DEBOUNCE_MS = 300;

function waitForDebounce(delay: number, signal: AbortSignal): Promise<void> {
  if (delay <= 0) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(resolve, delay);
    signal.addEventListener(
      "abort",
      () => {
        window.clearTimeout(timer);
        reject(signal.reason ?? new DOMException("Validation aborted", "AbortError"));
      },
      { once: true },
    );
  });
}

export async function runValidationRules<TValue>(
  value: TValue,
  rules: readonly ValidationRule<TValue>[],
  context: ValidationContext,
  bypassDebounce = false,
): Promise<ValidationIssue | undefined> {
  for (const rule of rules) {
    if (context.signal.aborted) {
      throw context.signal.reason ?? new DOMException("Validation aborted", "AbortError");
    }

    if (rule.kind === "async") {
      if (!bypassDebounce) {
        await waitForDebounce(
          rule.debounceMs ?? DEFAULT_ASYNC_VALIDATION_DEBOUNCE_MS,
          context.signal,
        );
      }
      const issue = await rule.validate(value, context);
      if (issue != null) {
        return issue;
      }
      continue;
    }

    const issue = rule.validate(value, context);
    if (issue != null) {
      return issue;
    }
  }

  return undefined;
}
